import { Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { firstValueFrom, of, timer } from 'rxjs';
import {
  catchError,
  map,
  scan,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs/operators';

export interface ServiceNotice {
  id?: string;
  revision?: number;
  title: string;
  message: string;
  instructions: string;
  kind: 'maintenance' | 'incident';
  scope: 'global' | 'organization';
  orgId: string | null;
  status: 'draft' | 'published' | 'resolved' | 'cancelled';
  startsAt: string;
  endsAt: string;
  showFrom: string;
  expiresAt: string;
  emailEnabled: boolean;
  history?: Array<ServiceNotice & { actorUid: string }>;
  emailJobs?: Array<{
    id: string;
    revision: number;
    status: string;
    accepted: number;
    failed: number;
    skipped: number;
    ambiguous: number;
  }>;
}
@Injectable({ providedIn: 'root' })
export class ServiceNoticesService {
  readonly active$ = this.auth.authState.pipe(
    switchMap(() =>
      timer(0, 60000).pipe(
        switchMap(() =>
          this.functions
            .httpsCallable<unknown, ServiceNotice[]>('listServiceNoticesV1')({})
            .pipe(catchError(() => of(null))),
        ),
        // Retain the last safe in-memory notice during a temporary outage, but reset it on account changes.
        scan(
          (previous: ServiceNotice[], current: ServiceNotice[] | null) =>
            current ?? previous,
          [],
        ),
        map((notices) =>
          notices.filter(
            (notice) => notice.expiresAt > new Date().toISOString(),
          ),
        ),
        startWith([] as ServiceNotice[]),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  constructor(
    private functions: AngularFireFunctions,
    private auth: AngularFireAuth,
  ) {}
  list(): Promise<ServiceNotice[]> {
    return firstValueFrom(
      this.functions.httpsCallable<unknown, ServiceNotice[]>(
        'adminListServiceNoticesV1',
      )({}),
    );
  }
  exportPublicSnapshot(): Promise<unknown> {
    return firstValueFrom(this.functions.httpsCallable('adminExportPublicStatusV1')({}));
  }
  save(
    notice: ServiceNotice,
  ): Promise<{ id: string; revision: number; emailQueued: boolean }> {
    return firstValueFrom(
      this.functions.httpsCallable<
        ServiceNotice,
        { id: string; revision: number; emailQueued: boolean }
      >('adminSaveServiceNoticeV1')(notice),
    );
  }
}
